import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Import axios for consistency with your Images page
import { Helmet } from "react-helmet";

const Dashboard = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [biblicalVerse, setBiblicalVerse] = useState({ text: '', reference: '' });
  const [biblicalVerseLoading, setBiblicalVerseLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [latestImages, setLatestImages] = useState([]);
  const [latestImagesLoading, setLatestImagesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const [selectedImageForModal, setSelectedImageForModal] = useState(null); // Stores the full image object now
  const [selectedImageIndex, setSelectedImageIndex] = useState(null); // New state for modal navigation index

  const API_BASE_URL =
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_URL_PRODUCTION
      : process.env.REACT_APP_API_URL;

  const SkeletonLoader = ({ type }) => {
    switch (type) {
      case 'card':
        return <div className="skeleton-loader-card"></div>;
      case 'text':
        return <div className="skeleton-loader-text"></div>;
      case 'paragraph':
        return (
          <>
            <div className="skeleton-loader-paragraph"></div>
            <div className="skeleton-loader-paragraph"></div>
            <div className="skeleton-loader-paragraph"></div>
          </>
        );
      case 'image-thumb':
        return <div className="skeleton-loader-image-thumb"></div>;
      default:
        return <div className="skeleton-loader-text"></div>;
    }
  };

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/dates`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        setDates(data);
      } catch (err) {
        console.error('Failed to load dates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchBiblicalVerse = async () => {
      setBiblicalVerseLoading(true);
      try {
        const res = await fetch('https://bible-api.com/?random=verse&translation=kjv');
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        if (data && data.text && data.reference) {
          setBiblicalVerse({ text: data.text.trim(), reference: data.reference });
        } else {
          setBiblicalVerse({ text: 'Could not fetch a verse today. Please try again later.', reference: '' });
        }
      } catch (err) {
        console.error('Failed to load biblical verse:', err);
        setBiblicalVerse({ text: 'Failed to load biblical verse. Check your internet connection or try again later.', reference: '' });
      } finally {
        setBiblicalVerseLoading(false);
      }
    };
    fetchBiblicalVerse();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
        setCategories(res.data || []);
      } catch (err) {
        console.error('Error fetching categories for dashboard:', err);
      }
    };
    fetchCategories();
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchLatestImages = async () => {
      setLatestImagesLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/images`);
        if (res.data) {
          const sortedImages = res.data.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          const imagesWithCategoryNames = sortedImages.slice(0, 10).map(img => ({
            ...img,
            categoryNames: img.imageCategories?.map(catObj => catObj.name).filter(Boolean).join(', ') || 'Uncategorized'
          }));
          setLatestImages(imagesWithCategoryNames);
        }
      } catch (err) {
        console.error('Failed to load latest images:', err);
      } finally {
        setLatestImagesLoading(false);
      }
    };
    fetchLatestImages();
  }, [API_BASE_URL]);

  const nextSlide = () => {
    if (sliderRef.current) {
      const itemWidth = 150 + 15;
      sliderRef.current.scrollBy({ left: itemWidth, behavior: 'smooth' });
    }
  };

  const prevSlide = () => {
    if (sliderRef.current) {
      const itemWidth = 150 + 15;
      sliderRef.current.scrollBy({ left: -itemWidth, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (sliderRef.current && latestImages.length > 0) {
        const { scrollLeft } = sliderRef.current;
        const itemWidth = 150 + 15;
        const newIndex = Math.round(scrollLeft / itemWidth);
        setCurrentSlide(newIndex);
      }
    };
    const el = sliderRef.current;
    el?.addEventListener('scroll', handleScroll);
    return () => el?.removeEventListener('scroll', handleScroll);
  }, [latestImages]);

  // New useEffect for Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && selectedImageForModal) {
        setSelectedImageForModal(null);
        setSelectedImageIndex(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedImageForModal]); // Dependency: re-run effect when selectedImageForModal changes

  const toSingaporeDate = (inputDate) => {
    const date = new Date(inputDate);
    const formatter = new Intl.DateTimeFormat('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
    const parts = formatter.formatToParts(date);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`);
  };

  const isSameMonthDay = (dateA, dateB) => {
    const a = toSingaporeDate(dateA);
    const b = toSingaporeDate(dateB);
    return a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };

  const getAge = (birthDateStr) => {
    const birthDate = toSingaporeDate(birthDateStr);
    const today = todayDate;
    let age = today.getFullYear() - birthDate.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
    return hasHadBirthdayThisYear ? age : age - 1;
  };

  const todayDate = toSingaporeDate(new Date());
  const nextWeekDate = new Date(todayDate);
  nextWeekDate.setDate(todayDate.getDate() + 7);

  const formatDisplayDate = (d) =>
    toSingaporeDate(d).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const todayEvents = dates.filter((d) => {
    const eventDate = toSingaporeDate(d.date);
    const isRecurring = d.isRecurring || d.classification === 'Birthday';
    return isRecurring
      ? isSameMonthDay(eventDate, todayDate)
      : eventDate.toDateString() === todayDate.toDateString();
  });

  const upcomingEvents = dates
    .filter((d) => {
      const eventDate = toSingaporeDate(d.date);
      const isRecurring = d.isRecurring || d.classification === 'Birthday';
      if (isRecurring) {
        const recurringThisYear = new Date(
          todayDate.getFullYear(),
          eventDate.getMonth(),
          eventDate.getDate()
        );
        return recurringThisYear > todayDate && recurringThisYear <= nextWeekDate;
      }
      return eventDate > todayDate && eventDate <= nextWeekDate;
    })
    .sort((a, b) => toSingaporeDate(a.date) - toSingaporeDate(b.date));

  const openImageModal = (image, index) => {
    setSelectedImageForModal(image);
    setSelectedImageIndex(index);
  };

  const navigateImageModal = (direction) => {
    if (selectedImageIndex === null || latestImages.length === 0) return;

    let newIndex = selectedImageIndex + direction;

    if (newIndex < 0) {
      newIndex = latestImages.length - 1;
    } else if (newIndex >= latestImages.length) {
      newIndex = 0;
    }

    setSelectedImageForModal(latestImages[newIndex]);
    setSelectedImageIndex(newIndex);
  };

  return (
    <div className="dashboard-container">
      <Helmet>
        <title>NavarroJalen | Dashboard</title>
        <meta name="description" content="NavarroJalen | Dashboard" />
        <meta property="og:title" content="NavarroJalen | Dashboard" />
      </Helmet>
      {isDrawerOpen && <div className="drawer-overlay" onClick={() => setIsDrawerOpen(false)}></div>}
      <div className="dashboardMainDiv">
        <div className="dashboard-navbar">
          <button className="dashboard-btn drawer-toggle-button" onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
            {isDrawerOpen ? 'Close APIs' : 'Events'}
          </button>
          <div className="dashboard-btn">Button</div>
          <div className="dashboard-btn">Button</div>
          <div className="dashboard-btn">Button</div>
          <div className="dashboard-btn">Button</div>
          <div className="dashboard-btn">Button</div>
          <div className="dashboard-btn">Button</div>
        </div>

        <div className="main-section-content">
          {loading ? (
            <div className="skeletonDiv">
              {Array(3).fill().map((_, i) => (
                <SkeletonLoader key={i} type="card" />
              ))}
            </div>
          ) : (
            <>
              <div className="latest-images-section eventSection">
                <h2>📸 Latest Uploaded Images</h2>
                {latestImagesLoading ? (
                  <div className="latest-images-slider-container skeleton-grid">
                    {Array(10).fill().map((_, i) => (
                      <SkeletonLoader key={i} type="image-thumb" />
                    ))}
                  </div>
                ) : latestImages.length > 0 ? (
                  <div className="latest-images-slider-wrapper">
                    <button className="slider-arrow prev" onClick={prevSlide}>&#10094;</button>
                    <div className="latest-images-slider-container" ref={sliderRef}>
                      {latestImages.map((img, index) => (
                        <div key={img._id} className="latest-image-card" onClick={() => openImageModal(img, index)}>
                          <img src={img.url} alt={img.name} />
                          <div className="image-details-overlay">
                            <p className="image-category-name">{img.categoryNames}</p>
                            <p className="image-name">{img.name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="slider-arrow next" onClick={nextSlide}>&#10095;</button>
                  </div>
                ) : (
                  <p>No images uploaded yet.</p>
                )}
              </div>

              <hr />

              <div className="recent-activities-section eventSection">
                <h2>Recent Activities</h2>
                <p>No recent activities to display.</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={`dashboardRightSideBar ${isDrawerOpen ? 'open' : ''}`}>
        <button className="drawer-close-button" onClick={() => setIsDrawerOpen(false)}>&times;</button>
        <div className="currentDateDisplay">
          <h3 className="eventSectionTitle">
            📅 Today is: <span className="dateToday">{formatDisplayDate(todayDate)}</span>
          </h3>
        </div>
        <hr />

        <div className="eventSection todayEventsSection">
          <h2>🎯 Today's Events</h2>
          {todayEvents.length === 0 ? (
            <p>No events today.</p>
          ) : (
            todayEvents.map((event) => (
              <div key={event._id} className="eventCard">
                <strong>{event.title}</strong> — {event.classification}
                {event.classification === 'Birthday' ? ` (${getAge(event.date)})` : ''}
              </div>
            ))
          )}
        </div>
        <hr />

        <div className="eventSection biblicalVerseSection">
          <h2>📖 Daily Scripture</h2>
          {biblicalVerseLoading ? (
            <SkeletonLoader type="paragraph" />
          ) : biblicalVerse.text ? (
            <>
              <p className="verseText">"{biblicalVerse.text}"</p>
              <p className="verseReference">— {biblicalVerse.reference}</p>
            </>
          ) : (
            <p>No verse available today.</p>
          )}
          {!biblicalVerseLoading && biblicalVerse.text && (
            <p className="api-attribution">
              Verse by{' '}
              <a href="https://bible-api.com/" target="_blank" rel="noopener noreferrer">
                Bible-API
              </a>
            </p>
          )}
        </div>
        <hr />

        <div className="eventSection upcomingEventsSection">
          <h2>📆 Upcoming Events</h2>
          {upcomingEvents.length === 0 ? (
            <p>No upcoming events.</p>
          ) : (
            upcomingEvents.map((event) => (
              <div key={event._id} className="eventCard">
                <strong>{event.title}</strong> — {event.classification}
                <br />
                On {formatDisplayDate(event.date)}
                <br />
                {event.description}
              </div>
            ))
          )}
        </div>
      </div>

      {selectedImageForModal && (
        <div className="modal-overlay full-image-modal-overlay" onClick={() => {
          setSelectedImageForModal(null);
          setSelectedImageIndex(null);
        }}>
          <div className="modal-content full-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => {
              setSelectedImageForModal(null);
              setSelectedImageIndex(null);
            }}>&times;</button>

            {latestImages.length > 1 && (
              <>
                <button className="nav-button prev-button" onClick={() => navigateImageModal(-1)}>&#10094;</button>
                <button className="nav-button next-button" onClick={() => navigateImageModal(1)}>&#10095;</button>
              </>
            )}

            <img src={selectedImageForModal.url} alt={selectedImageForModal.name} />

            <div className="modal-image-info">
                <h3>{selectedImageForModal.name}</h3>
                <p>{selectedImageForModal.description}</p>
                {selectedImageForModal.tags && selectedImageForModal.tags.length > 0 && (
                    <p>Tags: {selectedImageForModal.tags.join(', ')}</p>
                )}
                {selectedImageForModal.categoryNames && selectedImageForModal.categoryNames !== 'Uncategorized' && (
                    <p>Categories: {selectedImageForModal.categoryNames}</p>
                )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;